import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, getToken } from "@/lib/api";
import { BkPage, Card, SmallBtn, fmtMoney, fmtTime } from "@/components/bookkeeping/BkPage";

const BASE = process.env.REACT_APP_BACKEND_URL;
const input = "rounded-lg border border-[#333] bg-[#0F0F0F] px-2 py-1.5 text-xs text-white";
const STATUS = { reading: ["#A3A3A3", "Reading…"], unmatched: ["#F5B700", "Waiting for bank match"], review: ["#F5B700", "Needs review"], attached: ["#4CCC06", "Attached"], ignored: ["#666", "Ignored"] };
const FILTERS = [["", "All"], ["review", "Needs review"], ["unmatched", "Waiting for bank match"], ["attached", "Attached"], ["reading", "Reading"], ["ignored", "Ignored"]];

const openFile = async (id) => {
  const res = await fetch(`${BASE}/api/bookkeeping/receipts/${id}/file`, { headers: { Authorization: `Bearer ${getToken()}` } });
  if (!res.ok) return toast.error("Couldn't open the receipt");
  const url = URL.createObjectURL(await res.blob());
  window.open(url, "_blank", "noopener");
  setTimeout(() => URL.revokeObjectURL(url), 60000);
};

const InboxCard = () => {
  const [d, setD] = useState(null);
  const load = useCallback(() => api("/bookkeeping/receipts-inbox").then(setD).catch((e) => toast.error(e.message)), []);
  useEffect(() => { load(); }, [load]);
  const call = async (path, msg) => { try { const r = await api(path, { method: "POST", body: path.endsWith("connect") ? { origin_url: window.location.origin } : undefined }); if (r?.url) { window.location.href = r.url; return; } toast.success(msg); load(); } catch (e) { toast.error(e.message); } };
  const c = d?.connection; const on = c?.status === "connected";
  return (
    <Card title="RECEIPTS INBOX (GMAIL)" testId="bk-receipts-inbox">
      <div className="flex flex-wrap items-center gap-3 text-sm">
        <span className="min-w-0 flex-1 text-neutral-300" data-testid="bk-inbox-status">
          {!d ? "…" : on ? <>Connected to <b className="text-white">{c.email}</b>. Forward receipts there; checked every 15 minutes. Last checked {fmtTime(c.last_polled_at)}.</>
            : c?.status === "reconnect_required" ? <span className="text-[#FF6B6B]">Google sign-in expired. Reconnect the inbox.</span>
              : "Connect the Gmail inbox you forward receipts to. The portal only reads it; it never sends or deletes mail."}
        </span>
        {on && <SmallBtn onClick={() => call("/bookkeeping/receipts-inbox/poll", "Checking the inbox now; new receipts appear below shortly")} data-testid="bk-inbox-poll">Check inbox now</SmallBtn>}
        <SmallBtn primary={!on} disabled={d && !d.configured} onClick={() => call("/bookkeeping/receipts-inbox/connect")} data-testid="bk-inbox-connect">{on ? "Reconnect" : "Connect Gmail inbox"}</SmallBtn>
        {on && <SmallBtn onClick={() => window.confirm("Stop reading receipts from this inbox?") && call("/bookkeeping/receipts-inbox/disconnect", "Inbox disconnected")} data-testid="bk-inbox-disconnect">Disconnect</SmallBtn>}
      </div>
    </Card>
  );
};

const UploadCard = ({ entities, onDone }) => {
  const [eid, setEid] = useState("alr_equipment");
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const send = async (files) => {
    if (!files?.length) return;
    setBusy(true);
    try {
      const fd = new FormData(); [...files].forEach((f) => fd.append("files", f)); fd.append("entity_id", eid);
      const res = await fetch(`${BASE}/api/bookkeeping/receipts/upload`, { method: "POST", headers: { Authorization: `Bearer ${getToken()}` }, body: fd });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof j.detail === "string" ? j.detail : "Upload failed");
      j.errors?.forEach((e) => toast.error(`${e.file}: ${e.error}`));
      if (j.uploaded) toast.success(`${j.uploaded} receipt(s) uploaded; reading them now`);
      onDone();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };
  return (
    <Card title="UPLOAD RECEIPTS" testId="bk-receipts-upload">
      <div onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); send(e.dataTransfer.files); }}
        className="flex flex-wrap items-center gap-3 rounded-lg border border-dashed border-[#444] p-4 text-sm text-neutral-300" data-testid="bk-receipts-dropzone">
        <span className="min-w-0 flex-1">Drop PDFs or photos here (up to 20 at a time, 15 MB each), or</span>
        <select data-testid="bk-receipts-entity" value={eid} onChange={(e) => setEid(e.target.value)} className={input}>
          {entities.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
        </select>
        <SmallBtn primary disabled={busy} onClick={() => ref.current?.click()} data-testid="bk-receipts-choose">{busy ? "Uploading…" : "Choose files / take photo"}</SmallBtn>
        <input ref={ref} type="file" multiple accept="application/pdf,image/*" className="hidden" onChange={(e) => send(e.target.files)} data-testid="bk-receipts-file-input" />
      </div>
    </Card>
  );
};

const EditFields = ({ r, onSaved }) => {
  const ex = r.extracted || {};
  const [f, setF] = useState({ vendor: ex.vendor || "", date: ex.date || "", total: ex.total ?? "", tax: ex.tax ?? "" });
  const save = async () => {
    try {
      const body = { vendor: f.vendor || null, date: f.date || null, tax: f.tax === "" ? null : Number(f.tax) };
      if (!r.bank_transaction_id) body.total = f.total === "" ? null : Number(f.total);
      await api(`/bookkeeping/receipts/${r.id}`, { method: "PATCH", body });
      toast.success("Saved; looking for a match"); onSaved();
    } catch (e) { toast.error(e.message); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2">
      <input data-testid={`bk-rcpt-vendor-${r.id}`} value={f.vendor} onChange={(e) => setF({ ...f, vendor: e.target.value })} placeholder="Vendor" className={`${input} w-40`} />
      <input data-testid={`bk-rcpt-date-${r.id}`} type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} className={input} />
      <input data-testid={`bk-rcpt-total-${r.id}`} type="number" step="0.01" value={f.total} onChange={(e) => setF({ ...f, total: e.target.value })} placeholder="Total" className={`${input} w-24`} disabled={!!r.bank_transaction_id} />
      <input data-testid={`bk-rcpt-tax-${r.id}`} type="number" step="0.01" value={f.tax} onChange={(e) => setF({ ...f, tax: e.target.value })} placeholder="Tax" className={`${input} w-20`} />
      <SmallBtn primary onClick={save} data-testid={`bk-rcpt-save-${r.id}`}>Save</SmallBtn>
    </div>
  );
};

const Finder = ({ r, attach }) => {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState(null);
  const find = () => api(`/bookkeeping/receipts/${r.id}/candidates?q=${encodeURIComponent(q)}`).then(setRows).catch((e) => toast.error(e.message));
  return (
    <div className="space-y-1">
      <div className="flex flex-wrap gap-2">
        <input data-testid={`bk-rcpt-search-${r.id}`} value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && find()} placeholder="Search payee or amount" className={`${input} w-56`} />
        <SmallBtn onClick={find} data-testid={`bk-rcpt-find-${r.id}`}>Find bank transaction</SmallBtn>
      </div>
      {rows && (rows.length === 0 ? <p className="text-xs text-neutral-500">No money-out transactions found.</p> : rows.map((t) => (
        <div key={t.id} className="flex items-center gap-3 text-xs" data-testid={`bk-rcpt-cand-${r.id}-${t.id}`}>
          <span className="w-24 text-neutral-400">{t.posted_date}</span><span className="min-w-0 flex-1 text-white">{t.merchant_name || t.description}</span>
          <span className="font-bold text-white">{fmtMoney(t.amount)}</span><SmallBtn onClick={() => attach(t.id)} data-testid={`bk-rcpt-attach-${r.id}-${t.id}`}>Attach</SmallBtn>
        </div>
      )))}
    </div>
  );
};

const qboLine = (r) => (r.qbo_attachable_id ? `In QuickBooks on ${r.txn?.qbo_txn_type || "Purchase"} #${r.qbo_txn_id}` : r.txn?.qbo_txn_id ? "Uploading to QuickBooks…" : "Goes to QuickBooks when this expense is approved and posted");

const ReceiptRow = ({ r, reload }) => {
  const [edit, setEdit] = useState(false);
  const ex = r.extracted || {};
  const [c, label] = STATUS[r.status] || STATUS.review;
  const act = async (path, msg, body) => { try { await api(path, { method: "POST", body }); toast.success(msg); reload(); setTimeout(reload, 5000); } catch (e) { toast.error(e.message); } };
  const attach = (txn) => act(`/bookkeeping/receipts/${r.id}/attach`, "Attached", { txn_id: txn });
  return (
    <div className="space-y-2 py-3" data-testid={`bk-receipt-${r.id}`}>
      <div className="flex flex-wrap items-center gap-3 text-sm">
        <button onClick={() => openFile(r.id)} className="text-xs font-bold text-[#4CCC06] underline" data-testid={`bk-rcpt-open-${r.id}`}>{r.content_type === "application/pdf" ? "PDF" : r.content_type === "text/plain" ? "Email" : "Photo"}</button>
        <span className="min-w-0 flex-1 text-white"><b>{ex.vendor || r.filename}</b><span className="text-neutral-400"> · {ex.date || "no date"} · {ex.total != null ? fmtMoney(ex.total) : "no total"}{ex.tax != null ? ` (tax ${fmtMoney(ex.tax)})` : ""}</span>
          <span className="block text-xs text-neutral-500">{r.source === "email" ? `Emailed: ${r.email?.subject || ""} · ${r.email?.from || ""}` : `Uploaded by ${r.uploaded_by}`} · {fmtTime(r.created_at)}{ex.confidence != null && r.status !== "reading" ? ` · read ${Math.round(ex.confidence * 100)}% sure` : ""}</span>
        </span>
        <span className="rounded-full border px-2 py-0.5 text-[10px] font-extrabold" style={{ color: c, borderColor: c }} data-testid={`bk-rcpt-status-${r.id}`}>{label}</span>
      </div>
      {r.error && <p className="text-xs text-[#FF6B6B]" data-testid={`bk-rcpt-error-${r.id}`}>{r.error}</p>}
      {r.status === "attached" && r.txn && (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-[#262626] bg-[#0F0F0F] px-3 py-2 text-xs">
          <span className="min-w-0 flex-1 text-neutral-300">On <b className="text-white">{r.txn.merchant_name || r.txn.description}</b> · {r.txn.posted_date} · {fmtMoney(r.txn.amount)}{r.attached_by === "auto" ? " · matched automatically" : ""}
            <span className="block text-neutral-500" data-testid={`bk-rcpt-qbo-${r.id}`}>{qboLine(r)}</span></span>
          <SmallBtn onClick={() => act(`/bookkeeping/receipts/${r.id}/detach`, "Detached; back in review")} data-testid={`bk-rcpt-detach-${r.id}`}>Detach</SmallBtn>
        </div>
      )}
      {r.status !== "attached" && r.status !== "reading" && (
        <div className="space-y-2 rounded-lg border border-[#262626] bg-[#0F0F0F] px-3 py-2">
          {(r.proposals || []).map((p) => (
            <div key={p.bank_transaction_id} className="flex items-center gap-3 text-xs" data-testid={`bk-rcpt-prop-${r.id}-${p.bank_transaction_id}`}>
              <span className="min-w-0 flex-1 text-neutral-300">Possible match: <b className="text-white">{p.payee}</b> · {p.posted_date} · {fmtMoney(p.amount)} <span className="text-neutral-500">({Math.round(p.score * 100)}%)</span></span>
              <SmallBtn primary onClick={() => attach(p.bank_transaction_id)} data-testid={`bk-rcpt-accept-${r.id}-${p.bank_transaction_id}`}>Attach</SmallBtn>
            </div>
          ))}
          {r.status !== "ignored" && <Finder r={r} attach={attach} />}
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        <SmallBtn onClick={() => setEdit(!edit)} data-testid={`bk-rcpt-edit-${r.id}`}>{edit ? "Close" : "Fix details"}</SmallBtn>
        {r.status === "ignored" ? <SmallBtn onClick={() => act(`/bookkeeping/receipts/${r.id}/ignore?restore=true`, "Restored")} data-testid={`bk-rcpt-restore-${r.id}`}>Restore</SmallBtn>
          : r.status !== "attached" && <SmallBtn onClick={() => act(`/bookkeeping/receipts/${r.id}/ignore`, "Ignored")} data-testid={`bk-rcpt-ignore-${r.id}`}>Not a receipt</SmallBtn>}
      </div>
      {edit && <EditFields r={r} onSaved={() => { setEdit(false); reload(); }} />}
    </div>
  );
};

const Body = ({ entity, entities }) => {
  const [d, setD] = useState(null);
  const [filter, setFilter] = useState("");
  const load = useCallback(() => api(`/bookkeeping/receipts?entity_id=${entity.id}${filter ? `&status=${filter}` : ""}`).then(setD).catch((e) => toast.error(e.message)), [entity.id, filter]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { const g = new URLSearchParams(window.location.search).get("gmail"); if (g) (g === "connected" ? toast.success("Receipts inbox connected") : toast.error(`Inbox connection ${g}`)); }, []);
  const total = d ? Object.values(d.counts).reduce((a, b) => a + b, 0) : 0;
  return (
    <>
      <InboxCard />
      <UploadCard entities={entities} onDone={() => { load(); setTimeout(load, 20000); }} />
      <div className="flex flex-wrap items-center gap-2">
        {FILTERS.map(([k, l]) => <button key={k || "all"} onClick={() => setFilter(k)} data-testid={`bk-rcpt-filter-${k || "all"}`} className="rounded-full border px-3 py-1 text-xs font-bold" style={filter === k ? { background: "#4CCC06", color: "#000", borderColor: "#4CCC06" } : { color: "#D4D4D4", borderColor: "#333" }}>{l} · {k ? d?.counts?.[k] || 0 : total}</button>)}
        <span className="flex-1" />
        <SmallBtn onClick={() => api(`/bookkeeping/receipts/process?entity_id=${entity.id}`, { method: "POST" }).then(() => { toast.success("Matching receipts again"); setTimeout(load, 8000); }).catch((e) => toast.error(e.message))} data-testid="bk-rcpt-rematch">Match again</SmallBtn>
        <SmallBtn onClick={load} data-testid="bk-rcpt-refresh">Refresh</SmallBtn>
      </div>
      <Card title={`RECEIPTS · ${d?.rows.length ?? 0}`} testId="bk-receipts-list">
        {!d ? null : d.rows.length === 0 ? <p className="text-sm text-neutral-500" data-testid="bk-receipts-empty">No receipts here yet.</p>
          : <div className="divide-y divide-[#262626]">{d.rows.map((r) => <ReceiptRow key={r.id} r={r} reload={load} />)}</div>}
      </Card>
    </>
  );
};

export default function Receipts() {
  return <BkPage title="Receipts" testId="bookkeeping-receipts">{({ entity, entities }) => <Body entity={entity} entities={entities} />}</BkPage>;
}
